import React, { useState, useMemo } from 'react'
import styled from 'styled-components'
import { LandUsePlanDocument, DocumentSource } from '@popety-io/types'
import { SearchBar } from './SearchBar'
import { DocumentSection } from './DocumentSection'
import { DocumentListItem } from './DocumentListItem'

const BrowserContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`

const DocumentList = styled.div`
  flex: 1;
  overflow-y: auto;
`

export interface DocumentBrowserTranslations {
  searchPlaceholder?: string
  searchAriaLabel?: string
  clearSearchAriaLabel?: string
  noDocuments?: string
  sourceTitles?: Partial<Record<DocumentSource, string>>
  documentTypes?: {
    rule?: string
    map?: string
  }
  important?: string
}

const DEFAULT_SOURCE_TITLES: Record<DocumentSource, string> = {
  lupa: 'Local Plan Documents',
  lupc: 'Cantonal Plan Documents',
  lupo: 'Overlay Plan Documents',
  lup: 'Communal Plan Documents',
  region: 'Cantonal Documents',
  country: 'Federal Documents (Switzerland)',
}

const DEFAULT_TRANSLATIONS: DocumentBrowserTranslations = {
  searchPlaceholder: 'Search documents...',
  searchAriaLabel: 'Search documents',
  clearSearchAriaLabel: 'Clear search',
  noDocuments: 'No documents available',
  sourceTitles: DEFAULT_SOURCE_TITLES,
  documentTypes: {
    rule: 'rule',
    map: 'map',
  },
  important: 'Important',
}

interface DocumentBrowserProps {
  documentsBySource: Partial<Record<DocumentSource, LandUsePlanDocument[]>>
  precedenceOrder: DocumentSource[]
  selectedDocument: LandUsePlanDocument | null
  initialSource?: DocumentSource
  onSelectDocument: (document: LandUsePlanDocument) => void
  translations?: DocumentBrowserTranslations
  locale?: string
}

// Source icon configuration
const SOURCE_ICONS: Record<
  DocumentSource,
  'map-pin' | 'building' | 'map' | 'shield' | 'file-text'
> = {
  lupa: 'map-pin',
  lupc: 'building',
  lupo: 'map-pin',
  lup: 'file-text',
  region: 'map',
  country: 'shield',
}

export const DocumentBrowser: React.FC<DocumentBrowserProps> = ({
  documentsBySource,
  precedenceOrder,
  selectedDocument,
  initialSource = 'lupa',
  onSelectDocument,
  translations,
  locale,
}) => {
  // Merge translations with defaults
  const t = {
    ...DEFAULT_TRANSLATIONS,
    ...translations,
    sourceTitles: {
      ...DEFAULT_SOURCE_TITLES,
      ...translations?.sourceTitles,
    },
    documentTypes: {
      ...DEFAULT_TRANSLATIONS.documentTypes,
      ...translations?.documentTypes,
    },
  }

  const [searchQuery, setSearchQuery] = useState('')

  // Filter documents based on search query
  const filterDocuments = (documents: LandUsePlanDocument[]) => {
    if (!searchQuery.trim()) return documents

    const query = searchQuery.toLowerCase()
    return documents.filter(
      (doc) =>
        doc.title?.toLowerCase().includes(query) ||
        doc.document_type?.toLowerCase().includes(query) ||
        doc.language?.toLowerCase().includes(query)
    )
  }

  // Create filtered documents map
  const filteredDocumentsBySource = useMemo(() => {
    const filtered: Record<DocumentSource, LandUsePlanDocument[]> = {} as any

    precedenceOrder.forEach((source) => {
      const docs = documentsBySource[source] || []
      filtered[source] = filterDocuments(docs)
    })

    return filtered
  }, [documentsBySource, precedenceOrder, searchQuery])

  return (
    <BrowserContainer>
      <SearchBar
        value={searchQuery}
        onChange={setSearchQuery}
        translations={{
          placeholder: t.searchPlaceholder,
          searchAriaLabel: t.searchAriaLabel,
          clearAriaLabel: t.clearSearchAriaLabel,
        }}
      />

      <DocumentList>
        {precedenceOrder.map((source) => {
          const icon = SOURCE_ICONS[source]
          const title =
            t.sourceTitles?.[source] || DEFAULT_SOURCE_TITLES[source]
          const documents = filteredDocumentsBySource[source] || []

          return (
            <DocumentSection
              key={source}
              title={title}
              icon={icon}
              count={documents.length}
              isLoading={false}
              defaultExpanded={source === initialSource}
              translations={{
                noDocuments: t.noDocuments,
              }}
            >
              {documents.map((document) => (
                <DocumentListItem
                  key={document.id}
                  document={document}
                  isSelected={selectedDocument?.id === document.id}
                  onClick={() => onSelectDocument(document)}
                  translations={{
                    documentTypes: t.documentTypes,
                    important: t.important,
                  }}
                  locale={locale}
                />
              ))}
            </DocumentSection>
          )
        })}
      </DocumentList>
    </BrowserContainer>
  )
}
